import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Shared design tokens for the Onboarding Intelligence modal. Lifted from
 * inline `sx` blobs scattered across the five surface components so the
 * tokens live in one place and a future palette / spacing change is a
 * single-file edit.
 */

// Portal theme primary — Logicbroker brand blue. Passes AA on white (5.4:1)
// AND on the pale-blue button fill #EEF1FE (4.6:1).
export const PRIMARY_BLUE = '#006BFF';

// Darker navy for body / heading copy.
export const HEADING_NAVY = '#012237';

// Section / card title style — used at the top of every modal sub-card.
export const SECTION_LABEL_SX: SxProps<Theme> = {
    color: HEADING_NAVY,
    fontWeight: 700,
    fontFamily: 'Poppins',
    fontSize: '14px',
};

// Outer Card shell shared by Pipeline / Actions / Ready / Recommendations.
export const CARD_SX: SxProps<Theme> = {
    borderRadius: '8px',
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: 'none',
};

// Outlined-blue pill (mailto, Go To Settings, Update Priority, Push…).
export const PILL_BUTTON_SX: SxProps<Theme> = {
    borderRadius: '999px',
    textTransform: 'none' as const,
    color: PRIMARY_BLUE,
    borderColor: PRIMARY_BLUE,
    fontWeight: 500,
    padding: '2px 14px',
    whiteSpace: 'nowrap' as const,
};

/**
 * Per-status colors for the pipeline visualization. Two tokens per status:
 *
 *   `solid` — used for the legend dot. Must clear ~3:1 vs white for AA.
 *   `barFill` — used for the bar segment. Soft pastel; the bar itself is
 *               `aria-hidden` and decorative since the labeled legend and
 *               the visually-hidden <dl> carry the semantic meaning.
 *
 * Form Received was switched from yellow to violet so it cannot collide
 * with the amber Go Live Requested when both appear adjacent.
 *
 * `STATUS_PALETTE` is the single source of truth — `PipelineStatusKey` is
 * derived from it so adding a new status only requires editing this object.
 */
export const STATUS_PALETTE = {
    live: { solid: '#448044', barFill: '#C4DDC4', label: 'Live' },
    onboarding: { solid: '#4A78A8', barFill: '#C5D4E5', label: 'Onboarding' },
    goLiveRequested: {
        solid: '#C49A2A',
        barFill: '#EEDFBA',
        label: 'Go Live Requested',
    },
    formReceived: {
        // Violet — distinct from amber so adjacent segments don't bleed.
        solid: '#7A5FA8',
        barFill: '#D6CAE6',
        label: 'Form Received',
    },
    stalled: { solid: '#B85040', barFill: '#EAC4BC', label: 'Stalled 60+ days' },
    inviteSent: { solid: '#7C7280', barFill: '#D8D2DC', label: 'Invite Sent' },
} as const;

export type PipelineStatusKey = keyof typeof STATUS_PALETTE;

// Tile accent colors paired with the navy heading.
export const TILE_ACCENT = {
    total: HEADING_NAVY,
    ready: '#448044',
    highPriority: '#C44545',
} as const;

/**
 * Per-scenario accent for the Action Items dot indicators. These are distinct
 * from `STATUS_PALETTE` (which is for the pipeline bar) — Scenario 3 uses
 * amber for urgency on a card, while Form Received in the pipeline uses
 * violet to avoid clashing with the amber Go Live Requested segment.
 */
export const SCENARIO_ACCENT = {
    scenario1: TILE_ACCENT.highPriority,
    scenario2: TILE_ACCENT.ready,
    scenario3: '#F7C04A',
} as const;

// Hover state for the danger (close) button.
export const DANGER_RED_HOVER = '#A33636';
